// What is given to a class, for its instructor: the files of its Files tab
// (ClassFiles, RFC-054 §5) and the app templates of its Templates tab
// (ClassTemplates, RFC-052 §5) alike (byj-1wv.83). Each is listed as its own
// kind's row, taken back from its ⋯, asked first; one of the instructor's own
// is given to the class under them. Every member sees what is given.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../i18n";
import { useConfirm } from "./Confirm";
import type { MenuItem } from "./RowMenu";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** Where a kind's given things come from and go to, for one class. */
type GivenSource<T> = {
  /** the class's */
  given: () => Promise<T[]>;
  /** the instructor's own that could be given, given already or not */
  mine: () => Promise<T[]>;
  give: (id: string) => Promise<unknown>;
  take: (id: string) => Promise<unknown>;
};

/** Every word the tab says, its kind's. */
type GivenWords = {
  /** the ⋯ item and the ask's action */
  take: string;
  /** asked before one is taken back, by its `label` */
  takeAsk: (name: string) => string;
  givePick: string;
  give: string;
  noneToGive: string;
  none: string;
  noneLead: string;
};

export function ClassGiven<T extends { id: string; mine?: boolean }>({ of, source, onCount, row, label, words, testid }: {
  /** the class's id: read again when it changes, as useOpenInvites reads by its target's */
  of: string;
  /** the latest taken each render, so one made anew each render reads nothing again */
  source: GivenSource<T>;
  /** how many it found, for its tab's count */
  onCount?: (n: number) => void;
  /** one as its kind's row, `take` added to its ⋯ */
  row: (x: T, take: MenuItem[]) => ReactNode;
  /** one as an option to give */
  label: (x: T) => string;
  words: GivenWords;
  /** "class-file": the list class-files, none no-class-files, the form class-file-give */
  testid: string;
}) {
  // the class's: null until read, or when they could not be, and then said with Retry
  const [given, setGiven] = useState<T[] | null>(null);
  // mine, to give: null until read, or when they could not be, and then nothing is offered
  const [mine, setMine] = useState<T[] | null>(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  // a give or take back's own failure, said until the next press
  const [error, setError] = useState<string | null>(null);
  // why the last read of the class's, or of mine, failed: each said with Retry, the
  // class's in their place, mine over the list, so neither is taken for the other
  const [unread, setUnread] = useState<{ given?: string; mine?: string }>({});
  const [confirm, confirmDialog] = useConfirm();
  // the calls and the count's setter as last rendered, as Dismiss keeps its callback
  const latest = useRef({ source, onCount });
  latest.current = { source, onCount };
  // each read numbered, so an answer to one asked before a later one is dropped, as SessionWatch's
  const asked = useRef(0);
  // the class's and mine read apart, so one that fails leaves the other shown
  const read = useCallback(async () => {
    const n = ++asked.current;
    const { source: from, onCount: count } = latest.current;
    await Promise.all([
      from.given().then(
        (xs) => {
          if (n !== asked.current) return;
          setGiven(xs);
          setUnread(({ given: _, ...u }) => u);
          count?.(xs.length);
        },
        (err: unknown) => {
          if (n !== asked.current) return;
          setGiven(null);
          setUnread((u) => ({ ...u, given: errText(err) }));
        },
      ),
      from.mine().then(
        (xs) => {
          if (n !== asked.current) return;
          setMine(xs);
          setUnread(({ mine: _, ...u }) => u);
        },
        (err: unknown) => {
          if (n !== asked.current) return;
          setMine(null);
          setUnread((u) => ({ ...u, mine: errText(err) }));
        },
      ),
    ]);
  }, [of]);
  const retry = () => {
    setError(null);
    void read();
  };
  const retryButton = (
    <button className="small" onClick={retry}>
      {t("common:retry")}
    </button>
  );
  useEffect(() => {
    void read();
  }, [read]);

  /** True once done; false when it failed, said until the next press. */
  const act = async (f: () => Promise<unknown>): Promise<boolean> => {
    setError(null);
    let ok = true;
    try {
      await f();
    } catch (err) {
      setError(errText(err));
      ok = false;
    }
    await read();
    return ok;
  };
  const take = async (x: T) => {
    if (!(await confirm(words.takeAsk(label(x)), { action: words.take, danger: true }))) return;
    await act(() => latest.current.source.take(x.id));
  };
  const givable = (mine ?? []).filter((m) => !(given ?? []).some((x) => x.id === m.id));
  // one picked and since given (elsewhere, or by the last press) is no longer offered, as GiveToClass's
  const value = givable.some((x) => x.id === pick) ? pick : (givable[0]?.id ?? "");

  const giveForm =
    mine === null ? null : givable.length === 0 ? (
      <p className="muted small">{words.noneToGive}</p>
    ) : (
      <form
        className="row wrap give-form"
        data-testid={`${testid}-give`}
        onSubmit={(e) => {
          e.preventDefault();
          // sent once: a submit by Enter while one is sent is not a second give
          if (busy) return;
          setBusy(true);
          // failed, the one picked stays picked, to try again
          void act(() => latest.current.source.give(value)).then((ok) => {
            setBusy(false);
            if (ok) setPick("");
          });
        }}
      >
        <label className="grow">
          {words.givePick}
          {/* held while one is given, so the pick a give clears is the one it gave */}
          <select value={value} onChange={(e) => setPick(e.target.value)} disabled={busy}>
            {givable.map((x) => (
              <option key={x.id} value={x.id}>
                {label(x)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={busy}>
          {words.give}
        </button>
      </form>
    );

  return (
    <>
      {confirmDialog}
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {given === null ? (
        unread.given ? (
          // the class's could not be read: said with Retry, as a student's Templates tab does
          <p className="error">
            {t("common:error", { m: unread.given })} {retryButton}
          </p>
        ) : (
          // loading still, though mine may have failed already: that is said once the class's are here
          <p className="muted">{t("common:loading")}</p>
        )
      ) : (
        // mine could not be read: nothing is offered to give until they are
        unread.mine && (
          <p className="error">
            {t("common:error", { m: unread.mine })} {retryButton}
          </p>
        )
      )}
      {given === null ? null : given.length === 0 ? (
        <div className="empty-card" data-testid={`no-${testid}s`}>
          <h3 className="flush">{words.none}</h3>
          <p className="muted small">{words.noneLead}</p>
          {giveForm}
        </div>
      ) : (
        <>
          <ul className="rows" data-testid={`${testid}s`}>
            {given.map((x) => row(x, x.mine ? [{ label: words.take, onSelect: () => void take(x), danger: true }] : []))}
          </ul>
          {giveForm}
        </>
      )}
    </>
  );
}
